import { useEffect, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

interface CatalogCollapsibleFormSectionProps {
  title: string;
  icon: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  collapsible?: boolean;
}

export function CatalogCollapsibleFormSection({
  title,
  icon,
  children,
  defaultOpen = false,
  collapsible = true,
}: CatalogCollapsibleFormSectionProps) {
  const [mobileOpen, setMobileOpen] = useState(defaultOpen);
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return true;
    }
    return window.matchMedia("(min-width: 640px)").matches;
  });

  useEffect(() => {
    if (!collapsible || typeof window.matchMedia !== "function") return;
    const mediaQuery = window.matchMedia("(min-width: 640px)");
    const syncViewport = () => setIsDesktop(mediaQuery.matches);
    syncViewport();
    mediaQuery.addEventListener("change", syncViewport);
    return () => mediaQuery.removeEventListener("change", syncViewport);
  }, [collapsible]);

  if (!collapsible) {
    return (
      <Card className="overflow-hidden bg-white shadow-sm">
        <div className="p-6">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
            {icon}
            {title}
          </h2>
          {children}
        </div>
      </Card>
    );
  }

  const open = isDesktop ? true : mobileOpen;

  return (
    <Card className="overflow-hidden bg-white shadow-sm">
      <Collapsible open={open} onOpenChange={isDesktop ? undefined : setMobileOpen}>
        <CollapsibleTrigger
          className={cn(
            "flex w-full items-center justify-between p-6 text-left transition-opacity duration-200",
            isDesktop && "pointer-events-none cursor-default"
          )}
        >
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            {icon}
            {title}
          </h2>
          <ChevronDown
            className={cn(
              "h-5 w-5 shrink-0 text-gray-500 transition-transform duration-200 sm:hidden",
              open && "rotate-180"
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
          <div className="space-y-4 px-6 pb-6 pt-0">{children}</div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
